import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { StrategicImperative } from 'generated/maya.types';
import { strategicImperativeFragment } from 'modules/maya/graphql/fragments/strategicImperative.fragments';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const updateStrategicImperativeMutationCall = gql`
mutation updateStrategicImperative($id: String!, $updateStrategicImperative: UpdateStrategicImperativeInput!) {
  updateStrategicImperative(id: $id, updateStrategicImperativeInput: $updateStrategicImperative) {
    ${strategicImperativeFragment}
  }
}
`;

type ResultType = {
  updateStrategicImperative: StrategicImperative;
};

type UseUdpdateStrategicImperativeMutation = [
  (id: string | null, strategicImperative: Partial<StrategicImperative>) => Promise<StrategicImperative | undefined | null>,
  MutationResult<ResultType>,
];

export const useUpdateStrategicImperativeMutation = (): UseUdpdateStrategicImperativeMutation => {
  const [updateStrategicImperative, state] = useMutation<ResultType>(updateStrategicImperativeMutationCall, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });

  const executeMutation = useCallback(async (id, strategicImperative) => {
    try {
      const { data } = await updateStrategicImperative({
        variables: { id, updateStrategicImperative: strategicImperative },
      });
      return data?.updateStrategicImperative;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, strategicImperative);
        Logger.logError({
          error,
          customContext,
          transactionName: 'updateStrategicImperative.ts - useUpdateStrategicImperativeMutation',
        });
      }
      return null;
    }
  }, [updateStrategicImperative]);

  return [executeMutation, state];
};
